/**
 * docViewers/downloadOriginal — the single import point the viewers,
 * the ViewerRecovery panel and the DocViewerErrorBoundary fallback use
 * for the two non-render recovery actions:
 *
 *   1. `downloadOriginal(doc)` — save the raw file and open it locally.
 *      A thin pass-through to the shared `downloadDocument` in
 *      `src/lib/documentDownload.ts` (the proxy-authed GCS fetch + save).
 *      Re-exported, not re-implemented — exactly one download path —
 *      so viewers import it from here rather than reaching across the
 *      tree, and the dependency surface stays obvious. The
 *      DocViewerErrorBoundary (a class component) and the
 *      function-component viewers share this same symbol.
 *
 *   2. `viewExtractedText` — the "what the AI sees" opener. The actual
 *      modal is `ExtractedTextDialog` (hoisted in `DocViewerDialog`); the
 *      opener is the `onViewExtractedText` callback delivered through
 *      `DocViewerActionsContext` so a viewer rendered without that host
 *      (e.g. in a test) simply doesn't surface the button. Re-exported
 *      from here for discoverability — `<ViewerRecovery>` already wires
 *      this for the common panel-with-buttons case, and the
 *      DocViewerDialog header / DocViewerErrorBoundary fallback hold the
 *      callback directly.
 *
 * Kept dependency-light on purpose: no React import here, so this module
 * can't introduce a cycle with `useDocBytes` (which owns the context).
 */
import { downloadDocument } from '../../../lib/documentDownload';
import type { SiteDocument } from '../../../types/siteRag';

export { ExtractedTextDialog } from './ExtractedTextDialog';
export { DocViewerActionsContext, type DocViewerActions } from './useDocBytes';

/**
 * Download a document's original bytes and trigger a browser save.
 * Throws (with a "re-upload to enable download" message) when the doc
 * predates raw-file persistence and has no `rawStoragePath`.
 */
export function downloadOriginal(doc: SiteDocument): Promise<void> {
  return downloadDocument(doc);
}
